import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mic, MicOff, Volume2, RefreshCw, Sparkles, Trophy } from "lucide-react";
import { useSpeechRecognition, useSpeechSynthesis, scorePronunciation } from "../hooks/useSpeech";
import { startActivityTracking, stopActivityTracking } from "../lib/dailyPlan";
import { earnXP } from "../gamification";

const SOUNDS = [
  {
    sound: "th",
    title: "The 'th' sound",
    color: "from-rust to-rust-dark",
    desc: "Stick your tongue between your teeth. Two flavors: voiced (this) vs unvoiced (think).",
    practice: ["thank", "think", "this", "those", "three", "throw", "thin", "the", "with", "both"],
    sentences: ["This is the third thing", "I think Thursday is fine", "Three thieves with three thrones"],
  },
  {
    sound: "r",
    title: "The 'r' sound",
    color: "from-gold-500 to-gold-600",
    desc: "Curl tongue back, don't touch the roof. Hardest sound for many learners.",
    practice: ["red", "right", "around", "very", "really", "river", "road", "story", "bring", "rare"],
    sentences: ["Around the rugged rock", "Red roses really run", "The river runs right around"],
  },
  {
    sound: "l",
    title: "The 'l' sound",
    color: "from-sage to-sage-dark",
    desc: "Tip of tongue touches behind upper teeth. Often confused with 'r'.",
    practice: ["light", "love", "little", "yellow", "really", "milk", "world", "tell", "school", "feel"],
    sentences: ["Little Lily loves lollipops", "Yellow leaves all fall", "Tell the small girl hello"],
  },
  {
    sound: "w",
    title: "The 'w' sound",
    color: "from-sky to-sky-dark",
    desc: "Round your lips like 'oo' then quickly open. Don't say 'v'.",
    practice: ["water", "world", "want", "well", "where", "win", "white", "with", "wonder", "warm"],
    sentences: ["We want warm water", "Where will we walk?", "Wendy's white wedding was wonderful"],
  },
  {
    sound: "sh",
    title: "The 'sh' sound",
    color: "from-gold-500 to-gold-600",
    desc: "Push tongue back. Lips slightly pushed forward. Makes a 'shh' noise.",
    practice: ["shop", "she", "ship", "fish", "wish", "wash", "show", "shine", "sure", "machine"],
    sentences: ["She sells seashells", "Shy sheep should shower", "Shine my shoes shopkeeper"],
  },
  {
    sound: "v",
    title: "The 'v' sound",
    color: "from-ink-900 to-gold-600",
    desc: "Top teeth touch lower lip — buzzing sound. Don't say 'w' or 'b'.",
    practice: ["very", "voice", "video", "love", "five", "have", "live", "give", "river", "over"],
    sentences: ["Very vivid voices", "Vivian loves five vans", "I have to give you a video"],
  },
];

export default function PronunciationGym() {
  const [activeIdx, setActiveIdx] = useState(null);

  useEffect(() => {
    startActivityTracking("oral", "pronunciation-gym");
    return () => stopActivityTracking();
  }, []);

  if (activeIdx !== null) {
    return <SoundPractice sound={SOUNDS[activeIdx]} onBack={() => setActiveIdx(null)} />;
  }

  return (
    <div className="page-enter pb-12 space-y-6 relative">
      <div className="blob blob-purple" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.18 }} />
      <div className="blob blob-cyan" style={{ width: 260, height: 260, top: 280, left: -40, opacity: 0.15 }} />

      <div className="relative">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-gold-700 hover:text-gold-700 text-sm font-bold link-animate">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
        </Link>
        <h1 className="font-display text-4xl md:text-5xl font-normal mt-3"><em>Pronunciation gym.</em></h1>
        <p className="text-gray-600 dark:text-gray-300 text-sm font-semibold mt-1">
          Master the toughest English sounds. 6 focused workouts with mic feedback.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SOUNDS.map((s, idx) => (
          <button
            key={s.sound}
            onClick={() => setActiveIdx(idx)}
            className="group text-left relative overflow-hidden rounded-3xl bg-white dark:bg-ink-800 border-2 border-gray-100 dark:border-gray-700 hover:border-gold-500 dark:hover:border-gold-500 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 p-5"
          >
            <div className={`absolute -right-6 -top-6 w-32 h-32 bg-gradient-to-br ${s.color} rounded-full opacity-20 group-hover:opacity-40 group-hover:scale-150 transition-all duration-500 blur-xl`} />
            <div className={`relative inline-block w-14 h-14 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white font-black text-2xl shadow-lg mb-3 group-hover:scale-110 group-hover:rotate-6 transition-transform`}>
              {s.sound}
            </div>
            <h3 className="font-black text-base text-gray-900 dark:text-white">{s.title}</h3>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1.5 leading-relaxed">{s.desc}</p>
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{s.practice.length} words · {s.sentences.length} phrases</span>
              <Sparkles className="w-4 h-4 text-gold-700 group-hover:scale-125 transition-transform" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function SoundPractice({ sound, onBack }) {
  const [step, setStep] = useState(0); // 0 = words, 1 = sentences
  const [idx, setIdx] = useState(0);
  const [scores, setScores] = useState([]);
  const items = step === 0 ? sound.practice : sound.sentences;
  const current = items[idx];

  const { transcript, listening, start, stop, reset, supported: micSupported } = useSpeechRecognition({ lang: "en-US" });
  const { speak, speaking } = useSpeechSynthesis();

  useEffect(() => {
    if (!listening && transcript) {
      const r = scorePronunciation(current, transcript);
      setScores((s) => [...s, { word: current, said: transcript, score: r.score }]);
      earnXP(r.score >= 80 ? 8 : r.score >= 50 ? 5 : 3, "speaking");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening, transcript]);

  function nextItem() {
    reset();
    if (idx < items.length - 1) {
      setIdx(idx + 1);
    } else if (step === 0) {
      setStep(1);
      setIdx(0);
    } else {
      // Done
    }
  }

  const isComplete = step === 1 && idx === items.length - 1 && scores.length === sound.practice.length + sound.sentences.length;
  const lastScore = scores[scores.length - 1];

  if (isComplete) {
    const avg = Math.round(scores.reduce((a, b) => a + b.score, 0) / scores.length);
    return (
      <div className="page-enter pb-12 text-center max-w-md mx-auto">
        <div className="text-7xl mb-4 animate-bounce">🏆</div>
        <h1 className="text-3xl font-black mb-2"><span className="sparkle-text">Workout complete!</span></h1>
        <p className="text-gray-600 dark:text-gray-300 mb-6 font-semibold">You practiced "{sound.sound}" sound</p>

        <div className="glass-card p-6 mb-5 shadow-pop-indigo">
          <p className="text-xs uppercase tracking-wider font-black text-gray-500 mb-1">Average score</p>
          <p className="text-6xl font-black text-gold-700 mb-2">{avg}%</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">{scores.length} attempts</p>
        </div>

        <div className="space-y-2">
          <button onClick={onBack} className="btn-premium btn-premium-primary w-full py-3">
            <Trophy className="w-4 h-4" /> Try another sound
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter pb-12 space-y-5">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-gold-700 hover:text-gold-700 text-sm font-bold link-animate">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to sounds
      </button>

      <div className="text-center">
        <div className={`inline-block w-16 h-16 rounded-2xl bg-gradient-to-br ${sound.color} text-white font-black text-3xl flex items-center justify-center shadow-lg mb-3`}>
          {sound.sound}
        </div>
        <h1 className="text-2xl font-black"><span className="sparkle-text">{sound.title}</span></h1>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-bold uppercase tracking-wider">
          {step === 0 ? `Word ${idx + 1}/${items.length}` : `Sentence ${idx + 1}/${items.length}`}
        </p>
      </div>

      {/* Item card */}
      <div className="glass-card p-6 text-center">
        <p className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-white mb-3">
          "{current}"
        </p>
        <button
          onClick={() => speak(current, { lang: "en-US", rate: 0.7 })}
          disabled={speaking}
          className="btn-premium btn-premium-primary py-2.5 px-5 text-sm"
        >
          <Volume2 className="w-4 h-4" /> Hear it slow
        </button>
      </div>

      {/* Mic */}
      <div className="text-center">
        <button
          onClick={() => (listening ? stop() : start())}
          disabled={!micSupported}
          className={`relative w-24 h-24 rounded-full shadow-2xl mx-auto transition-all ${
            listening ? "bg-gradient-to-br from-berry-light0 to-rust-dark scale-110 animate-pulse" : "bg-gradient-to-br from-sage to-sage-dark hover:scale-105"
          }`}
        >
          {listening && <div className="absolute inset-0 rounded-full bg-berry/40 animate-ping" />}
          {listening ? <MicOff className="w-10 h-10 text-white mx-auto" /> : <Mic className="w-10 h-10 text-white mx-auto" />}
        </button>
        <p className="text-xs text-gray-500 mt-2 font-bold">{listening ? "🎙️ Speak now" : "Tap mic and say the word"}</p>
      </div>

      {/* Last result */}
      {lastScore && lastScore.word === current && (
        <div className={`rounded-2xl p-4 text-center ${
          lastScore.score >= 80 ? "bg-sage-light border-2 border-sage-light dark:bg-sage-dark/30" :
          lastScore.score >= 50 ? "bg-gold-50 border-2 border-gold-300 dark:bg-gold-700/30" :
          "bg-rust-light border-2 border-rust-light dark:bg-rust-dark/30"
        }`}>
          <p className="text-3xl font-black mb-1">{lastScore.score}%</p>
          <p className="text-xs text-gray-600 dark:text-gray-300 italic">You said: "{lastScore.said}"</p>
          <button onClick={nextItem} className="btn-premium btn-premium-primary px-5 py-2 mt-3 text-sm">
            {idx < items.length - 1 ? "Next →" : step === 0 ? "Now sentences →" : "Finish 🏆"}
          </button>
          <button onClick={() => { reset(); setScores((s) => s.slice(0, -1)); }} className="ml-2 inline-flex items-center gap-1 text-xs text-gray-500 underline">
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
        </div>
      )}
    </div>
  );
}
